import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { KebabMenuIcon } from '@repo/atomic-components';
import { HeaderIconButton } from './HeaderIconButton';

export interface ChatHeaderMenuItem {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

interface ChatHeaderMoreMenuProps {
  items: ChatHeaderMenuItem[];
}

/** Gap between the "…" button and the menu, and the menu's minimum distance from the viewport edge. */
const MENU_OFFSET_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

/** "…" button that holds the chat header actions when the chat panel is too narrow for the icons. */
export const ChatHeaderMoreMenu = ({ items }: ChatHeaderMoreMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const closeMenuAndFocusTrigger = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  /** Right-align the menu under the button as it attaches, so it's never painted in the wrong spot. */
  const placeMenu = useCallback((element: HTMLDivElement | null) => {
    menuRef.current = element;
    const trigger = triggerRef.current;
    if (!element || !trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const { width } = element.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
    element.style.left = `${Math.max(VIEWPORT_MARGIN_PX, Math.min(triggerRect.right - width, maxLeft))}px`;
    element.style.top = `${triggerRect.bottom + MENU_OFFSET_PX}px`;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The button toggles the menu itself.
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      closeMenu();
    };

    // Widening the panel swaps the button for the icon row; the menu must not outlive it.
    const trigger = triggerRef.current;
    const triggerObserver = new ResizeObserver(() => {
      if (!trigger?.offsetWidth) closeMenu();
    });
    if (trigger) triggerObserver.observe(trigger);

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', closeMenu);
      triggerObserver.disconnect();
    };
  }, [isOpen, closeMenu]);

  const focusItemAt = (index: number) => {
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    if (buttons.length === 0) return;
    buttons[(index + buttons.length) % buttons.length]?.focus();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (isOpen || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    event.preventDefault();
    setIsOpen(true);
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    const focusedIndex = buttons.findIndex((button) => button === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItemAt(focusedIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItemAt(focusedIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItemAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusItemAt(buttons.length - 1);
        break;
      case 'Escape':
      case 'Tab':
        event.preventDefault();
        closeMenuAndFocusTrigger();
        break;
    }
  };

  const handleSelect = (item: ChatHeaderMenuItem) => {
    // Focus goes back to "…" first, so a dialog the item opens returns focus there when it closes.
    closeMenuAndFocusTrigger();
    item.onSelect();
  };

  return (
    <>
      <HeaderIconButton
        ref={triggerRef}
        icon={<KebabMenuIcon aria-hidden />}
        tooltip="More actions"
        tooltipPosition="left"
        active={isOpen}
        hideTooltip={isOpen}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
      />
      {isOpen &&
        createPortal(
          <div
            ref={placeMenu}
            id={menuId}
            role="menu"
            aria-label="More actions"
            onKeyDown={handleMenuKeyDown}
            className="bg-theme-white border-theme-border-grey fixed z-[10001] min-w-47.5 overflow-hidden rounded-[4px] border py-1 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]"
          >
            {items.map((item, index) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                autoFocus={index === 0}
                onClick={() => handleSelect(item)}
                className="text-theme-black hover:bg-theme-highlight-green focus-visible:bg-theme-highlight-green hover:text-theme-dark-green focus-visible:text-theme-dark-green flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left text-sm whitespace-nowrap focus:outline-none"
              >
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" aria-hidden>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};
